/* ==========================================================================
   brand-a11y.js — Fazal Shawl
   Added by customization; NOT part of the Shopfinity vendor theme.

   The theme shows a loading overlay and disables the add-to-cart button
   during requests, so sighted users get clear feedback. Assistive technology
   gets nothing: no element is ever marked aria-busy and there is no live
   region, so a screen reader user clicking a filter hears silence for the
   ~3s the request takes and is never told the results changed.

   This adds the missing half without touching how the theme loads anything:
     - one polite live region, created once
     - aria-busy on the container that owns a visible loading overlay
     - an announcement when a request starts and when results settle,
       reading the theme's own product count so the message is real
     - the same treatment for add-to-cart

   Deliberately observation-based rather than hooking the theme's JS, so a
   Shopfinity update cannot break it and we are not re-implementing any
   vendor behaviour.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR_OVERLAY = '.loading-overlay';
  var SELECTOR_CONTAINER =
    '#ProductGridContainer, .collection, .facets-container, .search__results, .shopify-section';
  var SELECTOR_ATC = '.product-form__submit, button[name="add"]';

  /* ---------- live region ---------- */
  var live = document.createElement('div');
  live.id = 'brand-a11y-status';
  live.className = 'visually-hidden';
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('aria-atomic', 'true');
  live.setAttribute('role', 'status');

  function mount() {
    if (!document.getElementById('brand-a11y-status')) document.body.appendChild(live);
  }

  var lastMessage = '';
  var announceTimer;
  function announce(msg) {
    if (!msg || msg === lastMessage) return;
    lastMessage = msg;
    clearTimeout(announceTimer);
    // Small delay so a fast request that resolves immediately does not
    // produce a "loading / loaded" stutter in the screen reader queue.
    announceTimer = setTimeout(function () { live.textContent = msg; }, 180);
  }

  /* ---------- helpers ---------- */
  function visible(el) {
    if (!el || el.hidden) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function resultsSummary() {
    var count = document.querySelector('.product-count__text, .product-count, [class*="product-count"]');
    if (count && count.textContent.trim()) return count.textContent.trim().replace(/\s+/g, ' ');
    var cards = document.querySelectorAll('.product-card-wrapper').length;
    if (cards) return cards + (cards === 1 ? ' product' : ' products');
    return 'Results updated';
  }

  /* ---------- overlay watching ----------
     Idempotent by design rather than transition-based. The theme replaces the
     results container's DOM wholesale on a filter change, which destroys the
     element carrying aria-busy — a transition-only approach set the attribute
     once and then silently lost it mid-request. Re-asserting the correct state
     on every pass survives those swaps.

     `announced` gates the results message behind a real loading episode, so
     merely opening a page never announces a product count nobody asked for. */
  var wasBusy = false;
  var seenLoading = false;   // gate: never announce a count on first paint
  var lastSummary = null;

  function sync() {
    var overlays = document.querySelectorAll(SELECTOR_OVERLAY);
    var busyNow = false;

    for (var i = 0; i < overlays.length; i++) {
      var ov = overlays[i];
      var container = ov.closest(SELECTOR_CONTAINER) || ov.parentElement;
      if (!container) continue;

      if (visible(ov)) {
        busyNow = true;
        if (container.getAttribute('aria-busy') !== 'true') {
          container.setAttribute('aria-busy', 'true');
        }
      } else if (container.getAttribute('aria-busy') === 'true') {
        container.removeAttribute('aria-busy');
      }
    }

    if (busyNow && !wasBusy) {
      announce('Loading');
    }
    wasBusy = busyNow;

    /* Announce the settled result by watching the count itself rather than
       the busy->idle edge. The theme replaces the whole results container, so
       the overlay that was visible is destroyed and recreated mid-request and
       that edge is not reliably observable — in testing the live region stuck
       on "Loading" even though the filter had completed and the count had
       changed to "52 of 53 products". The count changing IS the event the
       user cares about, and it survives the swap. */
    if (!busyNow && seenLoading) {
      var summary = resultsSummary();
      if (summary !== lastSummary) {
        lastSummary = summary;
        announce(summary);
      }
    }
    if (busyNow) seenLoading = true;
  }

  /* Throttle to one pass per frame — the observer is broad, the work is not. */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; sync(); });
  }

  /* ---------- add to cart ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest(SELECTOR_ATC) : null;
    if (!btn || btn.disabled) return;

    btn.setAttribute('aria-busy', 'true');
    announce('Adding to cart');

    var started = Date.now();
    var poll = setInterval(function () {
      if (!btn.disabled) {
        clearInterval(poll);
        btn.removeAttribute('aria-busy');
        var bubble = document.querySelector('.cart-count-bubble span:not(.visually-hidden), .cart-count-bubble');
        var n = bubble ? bubble.textContent.trim().replace(/\s+/g, ' ') : '';
        announce(n ? 'Added to cart. Cart now has ' + n + '.' : 'Added to cart.');
      } else if (Date.now() - started > 15000) {
        // Never leave a control marked busy forever if a request stalls.
        clearInterval(poll);
        btn.removeAttribute('aria-busy');
        announce('Still working. Please check your connection.');
      }
    }, 150);
  }, true);

  /* ---------- boot ---------- */
  function init() {
    mount();
    sync();

    /* Two detectors, because neither alone is sufficient here.

       The MutationObserver reacts instantly to the container swap. But the
       theme brings its overlay up by changing computed display — measured
       going none -> block -> none across roughly 750ms to 1950ms of a filter
       request — and that transition did not reliably surface through an
       attribute-filtered observer, so announcements were silently missed.

       The interval is the backstop: a querySelectorAll plus a handful of
       getComputedStyle reads, four times a second. Negligible next to a
       page that already issues 244 requests, and it cannot miss a state the
       observer does not report. */
    new MutationObserver(schedule).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'aria-busy']
    });

    setInterval(sync, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
